import styled from "styled-components";
import { colors, getTypographyFontStyle, device } from "../../styles/global.style";

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${({ $isScrolled }) =>
    $isScrolled
      ? "rgba(255, 255, 255, 0.95)"
      : "rgba(255, 255, 255, 0.7)"};
  backdrop-filter: ${({ $isScrolled }) => ($isScrolled ? "blur(20px)" : "blur(12px)")};
  -webkit-backdrop-filter: ${({ $isScrolled }) => ($isScrolled ? "blur(20px)" : "blur(12px)")};
  box-shadow: ${({ $isScrolled }) =>
    $isScrolled
      ? "0 4px 30px rgba(10, 22, 40, 0.12)"
      : "0 1px 3px rgba(10, 22, 40, 0.05)"};
  border-bottom: 1px solid ${({ $isScrolled }) =>
    $isScrolled ? "rgba(10, 22, 40, 0.08)" : "transparent"};
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 24px;

  @media ${device.md} {
    padding: 16px 48px;
  }

  @media ${device.lg} {
    padding: 16px 64px;
  }
`;

const Logo = styled.img`
  height: 45px;
  width: auto;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.03);
    filter: brightness(1.1);
  }

  @media ${device.md} {
    height: 55px;
  }
`;

const NavMenu = styled.div`
  display: none;
  align-items: center;
  gap: 8px;

  @media ${device.md} {
    display: flex;
  }
`;

const NavItem = styled.button`
  ${getTypographyFontStyle({ variant: "body1", weight: 500 })};
  color: ${({ $isActive }) => ($isActive ? colors.secondary : "rgba(10, 22, 40, 0.75)")};
  background: ${({ $isActive }) =>
    $isActive ? "rgba(245, 166, 35, 0.1)" : "transparent"};
  border: none;
  cursor: pointer;
  padding: ${({ $mobile }) => ($mobile ? "16px 24px" : "10px 20px")};
  border-radius: 12px;
  position: relative;
  transition: all 0.3s ease;
  text-align: ${({ $mobile }) => ($mobile ? "left" : "center")};
  width: ${({ $mobile }) => ($mobile ? "100%" : "auto")};
  display: flex;
  align-items: center;
  justify-content: ${({ $mobile }) => ($mobile ? "flex-start" : "center")};
  gap: 8px;

  &::before {
    content: "";
    position: absolute;
    bottom: ${({ $mobile }) => ($mobile ? "12px" : "6px")};
    left: ${({ $isActive, $mobile }) => {
      if ($mobile) return "24px";
      return $isActive ? "20px" : "50%";
    }};
    width: ${({ $isActive, $mobile }) => {
      if ($mobile) return $isActive ? "40px" : "0";
      return $isActive ? "calc(100% - 40px)" : "0";
    }};
    height: 2px;
    background: ${colors.solarGradient};
    border-radius: 1px;
    transition: all 0.3s ease;
    transform: ${({ $isActive, $mobile }) => ($isActive || $mobile ? "none" : "translateX(-50%)")};
  }

  &:hover {
    color: ${colors.secondary};
    background: ${({ $isActive }) =>
      $isActive ? "rgba(245, 166, 35, 0.12)" : "rgba(10, 22, 40, 0.04)"};

    &::before {
      width: ${({ $mobile }) => ($mobile ? "40px" : "calc(100% - 40px)")};
      left: ${({ $mobile }) => ($mobile ? "24px" : "20px")};
      transform: none;
    }
  }

  &:active {
    transform: ${({ $mobile }) => ($mobile ? "none" : "scale(0.98)")};
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1.5px solid rgba(10, 22, 40, 0.15);
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;

  img {
    width: 22px;
    height: 22px;
    transition: transform 0.3s ease;
  }

  &:hover {
    border-color: rgba(245, 166, 35, 0.5);
    background: rgba(245, 166, 35, 0.08);
  }

  &:active {
    transform: scale(0.95);
  }

  @media ${device.md} {
    display: none;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 76px;
  left: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 20px 60px rgba(10, 22, 40, 0.15);
  border: 1px solid rgba(10, 22, 40, 0.08);
  display: flex;
  flex-direction: column;
  gap: 4px;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  transform: translateY(${({ $isOpen }) => ($isOpen ? "0" : "-10px")});
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;

  @media ${device.md} {
    display: none;
  }

  /* Backdrop overlay */
  &::before {
    content: "";
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(10, 22, 40, 0.3);
    z-index: -1;
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
    transition: all 0.3s ease;
    pointer-events: none;
  }
`;

export {
  NavbarContainer,
  NavInner,
  Logo,
  NavMenu,
  NavItem,
  MobileMenuButton,
  MobileMenu,
};
